import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { setCurrentUser } from './currentUserSlice';
import { addCompanyFile, setCompanyFiles } from './companyFilesSlice';
import { addPostFile, setPostFiles } from './postFilesSlice';
import { setCompanyPosts } from '@/store/slices/companyPostsSlice';
import type { UndecoratedFile } from '@flemingmetrics/api-client';
import { mapToKey } from '@/utils/arrays';

type FilesState = {
  byId: Record<string, UndecoratedFile>;
};

const initialState: FilesState = {
  byId: {},
};

export const filesSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    addFile: (state, action: PayloadAction<UndecoratedFile>) => {
      return getNextState(state, [action.payload]);
    },
    setFiles: (state, action: PayloadAction<UndecoratedFile[]>) => {
      return getNextState(state, action.payload);
    },
    deleteFile: (state, action: PayloadAction<{ fileId: number }>) => {
      const byId = { ...state.byId };
      const key = `${action.payload.fileId}`;
      if (byId[key]) delete byId[key];
      return { byId };
    },
    deleteFiles: (state, action: PayloadAction<{ fileIds: number[] }>) => {
      const byId = { ...state.byId };
      action.payload.fileIds.forEach((fileId) => {
        delete byId[`${fileId}`];
      });
      return { byId };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setCurrentUser, (state, action) => {
        return { byId: {}, byCompanyId: {}, byInvestorId: {} };
      })
      .addCase(addCompanyFile, (state, action) => {
        return getNextState(state, [action.payload.file]);
      })
      .addCase(setCompanyPosts, (state, action) => {
        return getNextState(
          state,
          mapToKey(
            mapToKey(action.payload, 'post').flatMap((p) => p.postFiles),
            'file'
          )
        );
      })
      .addCase(setCompanyFiles, (state, action) => {
        return getNextState(state, mapToKey(action.payload, 'file'));
      })
      .addCase(addPostFile, (state, action) => {
        return getNextState(state, [action.payload.file]);
      })
      .addCase(setPostFiles, (state, action) => {
        return getNextState(state, mapToKey(action.payload, 'file'));
      });
  },
});

function getNextState(state: FilesState, files: UndecoratedFile[]) {
  const byId = { ...state.byId };
  files.forEach((file) => {
    const key = `${file.id}`;
    byId[key] = file;
  });
  return { byId };
}

export const { addFile, setFiles, deleteFile, deleteFiles } =
  filesSlice.actions;

export default filesSlice.reducer;
